import { type PointerEvent, type ReactNode, type Ref } from 'react'
import styles from './ConversationDialog.module.css'
import { ModalDialog } from '@/components/ModalDialog'
import { FloatingIconButton } from '@/components/FloatingIconButton'
import { useSubject } from '@/hooks/rx-hooks'

export interface ConversationDialogProps {
  /**
   * The content to render in the dialog.
   */
  children?: ReactNode
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Callback when the dialog should close.
   */
  onClose?: () => unknown
  /**
   * Ref to the dialog scroller element.
   */
  ref?: Ref<HTMLDivElement>
  /**
   * ARIA label for the dialog.
   */
  'aria-label'?: string
  /**
   * ARIA labelledby for the dialog.
   */
  'aria-labelledby'?: string
  /**
   * ARIA describedby for the dialog.
   */
  'aria-describedby'?: string
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the close button.
   */
  closeTestId?: string
}

/**
 * Dialog for rendering a {@link ConversationLayout}.
 */
export function ConversationDialog({
  children,
  isOpen,
  onClose,
  ref,
  testId,
  closeTestId,
  ...rest
}: ConversationDialogProps) {
  const startDrag$ = useSubject<PointerEvent>()
  const startDrag = (e: PointerEvent) => {
    startDrag$.next(e)
  }
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      sheetSizing="stretch"
      size="lg"
      startDrag$={startDrag$}
      testId={testId}
      {...rest}
    >
      <div className={styles.root}>
        <div className={styles.header} onPointerDown={startDrag}>
          <FloatingIconButton
            label="Close"
            name="close"
            onPress={onClose}
            testId={closeTestId}
          />
        </div>
        <div ref={ref} className={styles.scroller}>
          {children}
        </div>
      </div>
    </ModalDialog>
  )
}
